import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  getKitchensByManagingBranchAPI,
  regionalUpdateKitchenOperationAPI,
} from '../api-services/StoreManagement.service';

const StoreManagementDataContext = createContext(null);

const initialKitchensRequest = {
  loading: true,
  data: null,
  success: null,
  errorMessage: '',
};

const initialUpdateRequest = {
  loading: false,
  data: null,
  success: null,
  errorMessage: '',
};

export function StoreManagementDataProvider({ children }) {
  const [kitchensByManagingBranchData, setKitchensByManagingBranchData] =
    useState(initialKitchensRequest);
  const [updateKitchenOperationData, setUpdateKitchenOperationData] = useState(initialUpdateRequest);

  const markKitchensListSatisfiedWithoutFetch = useCallback(() => {
    setKitchensByManagingBranchData({
      loading: false,
      data: null,
      success: true,
      errorMessage: '',
    });
  }, []);

  const fetchKitchensByManagingBranch = useCallback(async (params) => {
    setKitchensByManagingBranchData({
      loading: true,
      data: null,
      success: null,
      errorMessage: '',
    });

    try {
      const data = await getKitchensByManagingBranchAPI(params);

      if (data.hasError) {
        setKitchensByManagingBranchData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message || 'Failed to load kitchens.',
        });
        return data;
      }

      const payload = data.data || {};
      const ok = payload.success === true;

      setKitchensByManagingBranchData({
        loading: false,
        data,
        success: ok,
        errorMessage: ok ? '' : payload.CustomMessage || payload.message || 'Could not load store list.',
      });

      return data;
    } catch (error) {
      console.log('fetchKitchensByManagingBranch catch', error);
      setKitchensByManagingBranchData({
        loading: false,
        data: null,
        success: false,
        errorMessage: error?.message || 'Failed to load kitchens.',
      });
      return { hasError: true, message: error?.message };
    }
  }, []);

  const regionalUpdateKitchenOperation = useCallback(async (body) => {
    setUpdateKitchenOperationData({
      loading: true,
      data: null,
      success: null,
      errorMessage: '',
    });

    try {
      const data = await regionalUpdateKitchenOperationAPI(body);
      if (data.hasError) {
        setUpdateKitchenOperationData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message || 'Failed to update store status.',
        });
        return data;
      }

      const payload = data.data || {};
      const ok = payload.success === true;
      setUpdateKitchenOperationData({
        loading: false,
        data,
        success: ok,
        errorMessage: ok
          ? ''
          : payload.CustomMessage || payload.customMessage || payload.message || 'Could not update store status.',
      });
      return data;
    } catch (error) {
      console.log('regionalUpdateKitchenOperation catch', error);
      setUpdateKitchenOperationData({
        loading: false,
        data: null,
        success: false,
        errorMessage: error?.message || 'Failed to update store status.',
      });
      return { hasError: true, message: error?.message };
    }
  }, []);

  const value = useMemo(
    () => ({
      kitchensByManagingBranchData,
      fetchKitchensByManagingBranch,
      markKitchensListSatisfiedWithoutFetch,
      updateKitchenOperationData,
      regionalUpdateKitchenOperation,
    }),
    [
      kitchensByManagingBranchData,
      fetchKitchensByManagingBranch,
      markKitchensListSatisfiedWithoutFetch,
      updateKitchenOperationData,
      regionalUpdateKitchenOperation,
    ]
  );

  return (
    <StoreManagementDataContext.Provider value={value}>{children}</StoreManagementDataContext.Provider>
  );
}

export function useStoreManagementData() {
  const ctx = useContext(StoreManagementDataContext);
  if (ctx == null) {
    throw new Error('useStoreManagementData must be used within StoreManagementDataProvider');
  }
  return ctx;
}
